import { React, useState, useEffect } from "react";
import {
	Button,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Typography,
	ListItemIcon,
	Popover,
} from "@mui/material";
import { InsertEmoticon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
	useEditSubcategoryMutation,
	useCategoriesQuery,
} from "../../../services/queryHooks";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { validateTextField } from "../../../utils/validation";

export default function EditSubcategoryModal({
	isOpen,
	onClose,
	categoryGroupId,
	subcategory,
}) {
	const [name, setName] = useState("");
	const [emoji, setEmoji] = useState("");
	const [nameError, setNameError] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const { mutate, reset } = useEditSubcategoryMutation();
	const { t } = useTranslation();

	const { data: categories } = useCategoriesQuery();

	useEffect(() => {
		setName(subcategory.name || t(subcategory.translationKey));
		setEmoji(subcategory.emoji);
	}, [isOpen, subcategory, t]);

	const validateInputs = () => {
		let isValid = true;
		setNameError("");
		const textError = validateTextField(name, t, {
			fieldName: t("categoryName"),
		});

		if (textError) {
			setNameError(textError);
			isValid = false;
		} else if (
			categories
				.find((cat) => cat._id === categoryGroupId)
				.subcategories.some(
					(subcat) =>
						subcat._id !== subcategory._id &&
						(
							subcat.name || t(subcat.translationKey)
						)?.toLowerCase() === name.trim().toLowerCase()
				)
		) {
			setNameError(t("categoryNameExistsError"));
			isValid = false;
		}
		return isValid;
	};

	const handleCloseModal = () => {
		setNameError("");
		reset();
		onClose();
	};

	const handleEmojiClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleEmojiPopoverClose = () => {
		setAnchorEl(null);
	};

	const handleEmojiSelect = (emoji) => {
		setEmoji(emoji.native);
		handleEmojiPopoverClose();
	};

	const submitForm = (e) => {
		e.preventDefault();
		if (!validateInputs()) return;

		const nothingChanged =
			name.trim() ===
				(subcategory.name || t(subcategory.translationKey)).trim() &&
			emoji === subcategory.emoji;

		if (nothingChanged) {
			onClose();
			return;
		}

		mutate({
			categoryGroupId: categoryGroupId,
			subcategoryId: subcategory._id,
			name: name,
			emoji: emoji,
		});
		onClose();
	};

	return (
		<Dialog open={isOpen} onClose={onClose} disableEnforceFocus>
			<DialogTitle>
				{t("editCategory") +
					": " +
					(subcategory.name
						? subcategory.name
						: t(subcategory.translationKey))}
			</DialogTitle>
			<form noValidate onSubmit={submitForm}>
				<DialogContent>
					<TextField
						autoFocus
						label={t("name")}
						value={name}
						onChange={(e) => setName(e.target.value)}
						type="text"
						fullWidth
						error={nameError ? true : false}
						helperText={nameError}
					/>
					<FormControl
						sx={{ mt: 2, display: "flex", flexDirection: "row" }}
					>
						<Typography
							component="span"
							variant="subtitle1"
							gutterBottom
						>
							{t("pickCategoryIcon") + ":"}
						</Typography>
						<ListItemIcon
							sx={{
								cursor: "pointer",
								minWidth: "auto",
								color: "inherit",
								mr: 1,
							}}
							onClick={(e) => handleEmojiClick(e)}
						>
							{emoji ? (
								<span style={{ fontSize: 22, color: "null" }}>
									{emoji}
								</span>
							) : (
								<InsertEmoticon
									sx={{ width: "30.36px", ml: 1 }}
								/>
							)}
						</ListItemIcon>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal}>{t("cancel")}</Button>
					<Button
						type="submit"
						variant="contained"
						onClick={validateInputs}
					>
						{t("save")}
					</Button>
				</DialogActions>
			</form>
			<Popover
				disableEnforceFocus
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleEmojiPopoverClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<Picker
					data={data}
					onEmojiSelect={handleEmojiSelect}
					previewPosition="none"
				/>
			</Popover>
		</Dialog>
	);
}
